<template>
  <button
    :id="id"
    type="button"
    class="b-seats__seat btn-clear"
    data-bs-toggle="tooltip"
    data-bs-custom-class="tooltip-seats"
    :style="seatPosition"
    :class="{ 'is-companion': isCompanion }"
    :disabled="isDisabled"
    :title="tooltipText"
    @click="onClickSeat">
    <img
      v-if="isNotAvailableSeat && notAvailableSeatIconUrl"
      :src="notAvailableSeatIconUrl"
      alt="not available seat"
      class="b-seats__seat-shape b-seats__seat-shape--img" />
    <svg
      v-else
      class="b-seats__seat-shape"
      :class="svgClassName"
      viewBox="0 0 22 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      v-html="getSvgContent()"></svg>
  </button>

  <ModalOverlay
    v-model="showModal"
    background-class="b-pop-up--seats-special b-pop-up__holder"
    header-background-class="b-pop-up__header">
    <template #header>
      <div class="b-pop-up__header__title">{{ dictionary.chooseSeats }}</div>
    </template>
    <template #body>
      <div class="b-pop-up__content">
        <span>{{ dictionary.seatSpecialModalDescription }}</span>

        <ul>
          <li>
            <button
              type="button"
              class="btn btn-secondary"
              @click="onClickCancel">
              <span>{{ dictionary.seatSpecialModalButtonCancelText }}</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              class="btn btn-primary-orange"
              @click="onClickOK">
              <span>{{ dictionary.seatSpecialModalButtonSubmitText }}</span>
            </button>
          </li>
        </ul>
      </div>
    </template>
  </ModalOverlay>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { seatCodeTypes } from '../../../../lib/seatsTypes'
import { Tooltip } from 'bootstrap'
import ModalOverlay from './../../../components/ModalOverlay.vue'
import { useBookingStore } from './../../../store/BookingStore'
const store = useBookingStore()
const showModal = ref(false)
const emit = defineEmits(['choose-seat'])

const props = defineProps({
  seat: {
    default: () => {
      return null
    },
    type: Object,
  },
})

const svgClassName = computed(() => {
  let value = null
  const prefix = 'b-seats-types__'
  const { status } = props.seat
  const name = seatCodeTypes()[status]?.name

  if (name === 'Sold' || name === 'House') {
    value = 'reserved'
  } else if (name === 'Empty') {
    value = 'available'
  } else if (name === 'Reserved') {
    if (store.hasSeatsRemainingAllocate) {
      value = 'temporary'
    } else {
      value = 'selected'
    }
  }

  return `${prefix}${value}`
})

const seatPosition = computed(() => {
  const left = props.seat?.columnIndex
  return { '--left': `${left}` }
})

const id = computed(() => {
  return `${props.seat.physicalName}${props.seat.id}`
})

const tooltipText = computed(() => {
  return `${props.seat.physicalName}-${props.seat.id}`
})

const atLeastOneSpecialIsAllocated = computed(() => {
  const entries = store.getSelectedSeats.map((x) => `${x.physicalName}${x.id}`)
  return store.seatsSpecial.some((i) => entries.includes(i))
})

const isDisabled = computed(() => {
  const type = seatCodeTypes()[props.seat.status.toString()]
  if (!type) {
    return true
  }

  if (isCompanion() && atLeastOneSpecialIsAllocated.value) {
    return false
  }

  const types = ['Corridor', 'NotExist', 'Sold', 'House', 'Companion']
  return types.includes(type.name)
})

onMounted(async () => {
  await nextTick
  new Tooltip(document.getElementById(id.value))
})

const onClickSeat = () => {
  if (isSpecial()) {
    showModal.value = true
    return false
  }

  emit('choose-seat', props.seat)
}

const isCompanion = () => {
  const type = seatCodeTypes()[props.seat.status.toString()]
  return type?.name === 'Companion'
}

const isSpecial = () => {
  const type = seatCodeTypes()[props.seat.status.toString()]
  return type?.name === 'Special'
}

const isNotAvailableSeat = computed(() => {
  const name = seatCodeTypes()[props.seat.status]?.name
  return name === 'Sold' || name === 'House'
})

const notAvailableSeatIconUrl = computed(
  () => store.bookingProgress.selectedMovie?.notAvailableSeatIcon || null,
)

const getSvgContent = () => {
  if (props.seat.areaCategoryColor) {
    return seatCodeTypes(props.seat.areaCategoryColor)[
      props.seat.status.toString()
    ].icon
  }
  return seatCodeTypes()[props.seat.status.toString()].icon
}

const onClickCancel = () => {
  showModal.value = false
}

const onClickOK = () => {
  emit('choose-seat', props.seat)
  showModal.value = false
}
</script>

<style lang="scss">
.tooltip-seats {
  &.bs-tooltip-auto {
    &[data-popper-placement^='top'] {
      .tooltip-arrow {
        &::before {
          border-top-color: var(--c-background-midnight-blue);
        }
      }
    }
  }

  .tooltip-inner {
    color: white;
    background: var(--c-background-midnight-blue);
  }
}
</style>

<style scoped lang="scss">
.b-pop-up__content ul {
  display: flex;
  align-items: center;
  gap: 20px;
  justify-content: space-between;

  li {
    flex: 1;
  }

  button {
    width: 100%;
  }
}
</style>
